/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, Input, OnDestroy, OnInit, signal,
} from '@angular/core';
import { UIRouterModule } from '@uirouter/angular';
import { FeedStoreService } from '../services/feed-store.service';
import { ActivityService } from './activity.service';
import { ActivityItemComponent } from './activity-item.component';
import {
  ActivityContext, ActivityView, toActivityView,
} from './activity-view';

type ActivityFilter = 'all' | 'projects' | 'community';

export const ACTIVITY_FILTERS: { value: ActivityFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'projects', label: 'Projects' },
  { value: 'community', label: 'Community' },
];

// Wait before clearing unread activity, as the AngularJS page did, so the nav badge doesn't flicker on arrival.
const CLEAR_DELAY_MS = 1000;

// The /activity page in the 2026 "Notifications" design, routed by UTEST_DESIGN_REFRESH_2026.
// `compact` is the same card on the Home page: no page padding, "View More" opens /activity,
// and activity stays unread until the tester visits /activity.
@Component({
  selector: 'ut-activity-feed-page',
  standalone: true,
  imports: [UIRouterModule, ActivityItemComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './activity-feed-page.component.html',
  styleUrls: ['./activity-feed-page.component.scss'],
})
export class ActivityFeedPageComponent implements OnInit, OnDestroy {
  @Input() compact = false;

  readonly filters = ACTIVITY_FILTERS;

  filter = signal<ActivityFilter>('all');
  views: ActivityView[] = [];
  // The newest this-many items in "All" are unread, as the AngularJS dashboard worked it out.
  unreadCount = 0;
  announcement = '';
  stores: Record<'all' | 'projects' | 'community', any>;

  private context: ActivityContext;
  private clearTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(
    @Inject('UserService') private UserService: any,
    @Inject('ConfigService') private ConfigService: any,
    @Inject('GamificationService') GamificationService: any,
    private activityService: ActivityService,
    private feedStore: FeedStoreService,
    private cdr: ChangeDetectorRef,
    private host: ElementRef<HTMLElement>,
  ) {
    this.context = { testerAppUrl: '', badgesById: GamificationService.badgesById || {} };
  }

  ngOnInit(): void {
    this.stores = {
      all: this.feedStore.create((params) => this.activityService.feed(params)),
      projects: this.feedStore.create((params) => this.activityService.projectActivities(params)),
      community: this.feedStore.create((params) => this.activityService.communityActivities(params)),
    };
    this.track(this.stores.all.load().then(() => {
      if (!this.compact) this.clearTimer = setTimeout(() => this.clearUnread(), CLEAR_DELAY_MS);
    }));
    this.track(this.stores.projects.load());
    this.track(this.stores.community.load());

    this.UserService.me().then((res) => {
      this.context = { ...this.context, currentUserId: res?.data?.id };
      this.unreadCount = res?.data?.unread_activities_count || 0;
      this.refresh();
    });
    this.ConfigService.config().then((res) => {
      this.context = { ...this.context, testerAppUrl: res.data.profile_url };
      this.refresh();
    });
  }

  ngOnDestroy(): void {
    clearTimeout(this.clearTimer);
  }

  get store(): any {
    return this.stores[this.filter()];
  }

  isUnread(index: number): boolean {
    return this.filter() === 'all' && index < this.unreadCount;
  }

  setFilter(value: string): void {
    this.filter.set(value as ActivityFilter);
    this.refresh();
    this.announcement = `Showing ${ACTIVITY_FILTERS.find((f) => f.value === value)?.label.toLowerCase()} activity`;
  }

  // View More can disappear on the last page, so focus moves to the first new item.
  loadMore(): void {
    const firstNew = this.views.length;
    this.track(this.store.loadMore().then(() => {
      this.announcement = `Loaded ${this.views.length - firstNew} more`;
      setTimeout(() => this.host.nativeElement.querySelectorAll<HTMLElement>('.activity__item')[firstNew]?.focus());
    }));
  }

  // Marks everything read, then refreshes the cached user so the nav's unread indicator updates.
  private clearUnread(): void {
    this.activityService.clearAll().then(() => {
      this.unreadCount = 0;
      this.UserService.me(30);
    }, () => {
      // Best effort: the items stay unread and are cleared on the next visit.
    });
  }

  // Rebuilt only when the items, filter or context change, so rows keep their identity between checks.
  private refresh(): void {
    this.views = this.store.feedItems.map((a) => toActivityView(a, this.context)).filter(Boolean);
    this.cdr.markForCheck();
  }

  // FeedStore mutates in place, so re-render when a page starts and when it lands.
  private track(promise: Promise<unknown>): void {
    this.cdr.markForCheck();
    promise.then(() => this.refresh());
  }
}
